import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ClipboardList, CheckCircle, Loader2, Brain, Award, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CounselorTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTask, setActiveTask] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showResult, setShowResult] = useState(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/student/counselor-tasks`);
      setTasks(res.data.tasks || []);
    } catch { toast.error('Failed to load tasks'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const openTask = (task) => {
    setActiveTask(task);
    setAnswers({});
    setShowResult(null);
  };

  const handleSubmit = async () => {
    if (!activeTask) return;
    const unanswered = activeTask.question_paper?.filter(q => !answers[String(q.id)]?.trim());
    if (unanswered?.length > 0) {
      toast.error(`Please answer all questions (${unanswered.length} remaining)`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/student/counselor-task/submit`, {
        assignment_id: activeTask.id, answers
      });
      setShowResult(res.data.submission);
      toast.success('Task submitted and evaluated!');
      fetchTasks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Submission failed'); }
    finally { setSubmitting(false); }
  };

  const renderQuestion = (q) => {
    const qid = String(q.id);
    const type = (q.type || 'essay').toLowerCase();

    return (
      <Card key={qid} className="p-4 border" data-testid={`task-q-${qid}`}>
        <div className="flex items-start gap-2 mb-3">
          <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-1 rounded">Q{q.id}</span>
          <div className="flex-1">
            <p className="text-sm font-medium">{q.question}</p>
            <div className="flex gap-2 mt-1"><Badge variant="outline" className="text-xs capitalize">{type.replace('_',' ')}</Badge><Badge variant="outline" className="text-xs">{q.marks} marks</Badge></div>
          </div>
        </div>

        {type === 'mcq' && q.options?.length > 0 && (
          <div className="space-y-2 ml-8">
            {q.options.map((opt, oi) => (
              <label key={oi} className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-all ${answers[qid] === opt ? 'border-teal-400 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input type="radio" name={`q-${qid}`} value={opt} checked={answers[qid] === opt} onChange={() => setAnswers({...answers, [qid]: opt})} className="accent-teal-600" />
                <span className="text-sm">{opt}</span>
              </label>
            ))}
          </div>
        )}

        {type === 'true_false' && (
          <div className="flex gap-3 ml-8">
            {['True', 'False'].map(opt => (
              <label key={opt} className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all ${answers[qid] === opt ? 'border-teal-400 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input type="radio" name={`q-${qid}`} value={opt} checked={answers[qid] === opt} onChange={() => setAnswers({...answers, [qid]: opt})} className="accent-teal-600" />
                <span className="text-sm">{opt}</span>
              </label>
            ))}
          </div>
        )}

        {type === 'fill_blank' && (
          <div className="ml-8"><Input value={answers[qid] || ''} onChange={e => setAnswers({...answers, [qid]: e.target.value})} placeholder="Your answer..." className="max-w-md" /></div>
        )}

        {(type === 'essay' || type === 'short_answer') && (
          <div className="ml-8"><Textarea value={answers[qid] || ''} onChange={e => setAnswers({...answers, [qid]: e.target.value})} placeholder="Write your answer..." rows={type === 'essay' ? 5 : 2} /></div>
        )}
      </Card>
    );
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>;

  return (
    <div className="space-y-4" data-testid="counselor-tasks">
      {tasks.length === 0 ? (
        <Card className="p-12 text-center"><ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">No counselor tasks assigned yet</p></Card>
      ) : (
        <div className="grid gap-4">
          {tasks.map(task => (
            <Card key={task.id} className={`p-5 border-0 shadow-lg ${task.submitted ? 'bg-green-50/50' : 'bg-white/90'}`} data-testid={`task-${task.id}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold">{task.title}</h3>
                    <Badge variant="outline" className="capitalize">{task.type}</Badge>
                    <Badge className="bg-teal-100 text-teal-700">{task.question_paper?.length || 0} Q</Badge>
                    {task.submitted && <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>}
                  </div>
                  {task.description && <p className="text-sm text-gray-600 mb-1">{task.description}</p>}
                  <p className="text-xs text-gray-400">By {task.created_by_name} | {task.total_marks} marks{task.due_date ? ` | Due: ${new Date(task.due_date).toLocaleDateString()}` : ''}</p>
                </div>
                {!task.submitted ? (
                  <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => openTask(task)} data-testid={`start-task-${task.id}`}>
                    <ArrowRight className="w-4 h-4 mr-1" /> Start
                  </Button>
                ) : task.submission?.ai_evaluation ? (
                  <Button size="sm" variant="outline" onClick={() => setShowResult(task.submission)} data-testid={`view-result-${task.id}`}>
                    <Brain className="w-4 h-4 mr-1" /> View Report
                  </Button>
                ) : (
                  <Badge className="bg-amber-100 text-amber-700">Evaluating...</Badge>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Task Dialog */}
      <Dialog open={!!activeTask && !showResult} onOpenChange={() => setActiveTask(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{activeTask?.title}</DialogTitle></DialogHeader>
          {activeTask?.description && <p className="text-sm text-gray-600">{activeTask.description}</p>}
          <div className="space-y-4 py-2">
            {activeTask?.question_paper?.map(q => renderQuestion(q))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActiveTask(null)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleSubmit} disabled={submitting} data-testid="submit-task-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              {submitting ? 'Submitting & Evaluating...' : 'Submit Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result Dialog */}
      <Dialog open={!!showResult} onOpenChange={() => { setShowResult(null); setActiveTask(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Award className="w-5 h-5 text-amber-500" /> Task Evaluation Report</DialogTitle></DialogHeader>
          {showResult && (
            <div className="space-y-4 py-2" data-testid="task-result">
              <Card className={`p-4 ${(showResult.score_percentage || 0) >= 70 ? 'bg-green-50 border-green-200' : (showResult.score_percentage || 0) >= 40 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex justify-between items-center">
                  <div><p className="text-2xl font-bold">{showResult.score_percentage || 0}%</p><p className="text-xs text-gray-500">Score: {showResult.earned_marks || 0}/{showResult.total_marks || 0}</p></div>
                  <Award className={`w-10 h-10 ${(showResult.score_percentage || 0) >= 70 ? 'text-green-500' : 'text-amber-500'}`} />
                </div>
              </Card>

              {showResult.ai_evaluation && (
                <>
                  {/* Per-Question Score Justification */}
                  {showResult.ai_evaluation.question_feedback?.length > 0 && (
                    <Card className="p-4">
                      <h4 className="font-semibold text-sm mb-3">Question-by-Question Scoring</h4>
                      <div className="space-y-3">
                        {showResult.ai_evaluation.question_feedback.map((qf, i) => (
                          <div key={i} className="p-3 bg-gray-50 rounded-lg border">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-sm font-medium">Q{qf.question_id || i + 1}</span>
                              <Badge className={`${(qf.score / qf.max_marks) >= 0.7 ? 'bg-green-100 text-green-700' : (qf.score / qf.max_marks) >= 0.4 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                                {qf.score}/{qf.max_marks}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-700">{qf.feedback}</p>
                            {qf.justification && <p className="text-xs text-gray-500 mt-1 italic border-l-2 border-gray-300 pl-2">{qf.justification}</p>}
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}

                  {showResult.ai_evaluation.overall_feedback && (
                    <Card className="p-4"><h4 className="font-semibold text-sm mb-2">Overall Feedback</h4><p className="text-sm text-gray-700">{showResult.ai_evaluation.overall_feedback}</p></Card>
                  )}
                  {showResult.ai_evaluation.strengths?.length > 0 && (
                    <Card className="p-4 border-green-100"><h4 className="font-semibold text-sm mb-2 text-green-700">Strengths</h4><ul className="space-y-1">{showResult.ai_evaluation.strengths.map((s, i) => <li key={i} className="text-sm flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />{s}</li>)}</ul></Card>
                  )}
                  {showResult.ai_evaluation.areas_for_improvement?.length > 0 && (
                    <Card className="p-4 border-amber-100"><h4 className="font-semibold text-sm mb-2 text-amber-700">Areas for Improvement</h4><ul className="space-y-1">{showResult.ai_evaluation.areas_for_improvement.map((a, i) => <li key={i} className="text-sm flex items-start gap-2"><ArrowRight className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />{a}</li>)}</ul></Card>
                  )}
                  {showResult.ai_evaluation.suggestions?.length > 0 && (
                    <Card className="p-4 border-indigo-100"><h4 className="font-semibold text-sm mb-2 text-indigo-700">Suggestions</h4><ul className="space-y-1">{showResult.ai_evaluation.suggestions.map((s, i) => <li key={i} className="text-sm flex items-start gap-2"><Brain className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />{s}</li>)}</ul></Card>
                  )}
                  {showResult.ai_evaluation.wellness_insights && (
                    <Card className="p-4 bg-purple-50 border-purple-200"><h4 className="font-semibold text-sm mb-2 text-purple-700">Wellness Insights</h4><p className="text-sm text-purple-800">{showResult.ai_evaluation.wellness_insights}</p></Card>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CounselorTasks;
